import {
  EmploymentType,
  JobPostRequirementOperator,
  JobPostRequirementSeverity,
  JobPostRequirementType,
  JobPostVisibility,
  PracticeSetting,
  WageFrequency,
} from "@api/shared-runtime/enums";
import { z } from "zod";

// ─── Requirement row shape (client-side) ─────────────────────────────────────

export type RequirementValue =
  | { kind: "free_text"; text: string }
  | { kind: "state_license"; states: string[] }
  | { kind: "professional_type"; ids: string[] }
  | { kind: "specialty"; ids: string[] };

export interface RequirementRow {
  /** Client-side stable key for list rendering */
  _key: string;
  type: JobPostRequirementType;
  operator: JobPostRequirementOperator;
  severity: JobPostRequirementSeverity;
  displayLabel: string;
  value: RequirementValue;
}

export function makeRequirementKey(): string {
  return `req-${Math.random().toString(36).slice(2)}`;
}

export function defaultValueForType(
  type: JobPostRequirementType,
): RequirementValue {
  switch (type) {
    case JobPostRequirementType.FreeText:
      return { kind: "free_text", text: "" };
    case JobPostRequirementType.StateLicense:
      return { kind: "state_license", states: [] };
    case JobPostRequirementType.ProfessionalType:
      return { kind: "professional_type", ids: [] };
    case JobPostRequirementType.Specialty:
      return { kind: "specialty", ids: [] };
  }
}

export function defaultOperatorForType(
  type: JobPostRequirementType,
): JobPostRequirementOperator {
  const map: Record<JobPostRequirementType, JobPostRequirementOperator> = {
    [JobPostRequirementType.FreeText]: JobPostRequirementOperator.Matches,
    [JobPostRequirementType.StateLicense]: JobPostRequirementOperator.HasAny,
    [JobPostRequirementType.ProfessionalType]:
      JobPostRequirementOperator.HasAny,
    [JobPostRequirementType.Specialty]: JobPostRequirementOperator.HasAny,
  };
  return map[type];
}

export function makeFreeTextRow(text: string): RequirementRow {
  return {
    _key: makeRequirementKey(),
    type: JobPostRequirementType.FreeText,
    operator: JobPostRequirementOperator.Matches,
    severity: JobPostRequirementSeverity.Flag,
    displayLabel: text,
    value: { kind: "free_text", text },
  };
}

/** Serialize a row's value for the API wire format */
export function serializeRequirementValue(
  value: RequirementValue,
): Record<string, unknown> {
  switch (value.kind) {
    case "free_text":
      return { text: value.text };
    case "state_license":
      return { states: value.states };
    case "professional_type":
      return { healthcare_role_ids: value.ids };
    case "specialty":
      return { specialty_ids: value.ids };
  }
}

export function requirementRowIsValid(row: RequirementRow): boolean {
  switch (row.value.kind) {
    case "free_text":
      return row.value.text.trim().length > 0;
    case "state_license":
      return row.value.states.length > 0;
    case "professional_type":
      return row.value.ids.length > 0;
    case "specialty":
      return row.value.ids.length > 0;
  }
}
export enum DayOfWeek {
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
  SUNDAY = "sunday",
}

export enum JobLocationType {
  InPerson = "in_person",
  Remote = "remote",
  Hybrid = "hybrid",
}

export const scheduleSchema = z.object({
  daysOfWeek: z.array(z.nativeEnum(DayOfWeek)),
  startTime: z.string().optional().nullable(),
  endTime: z.string().optional().nullable(),
});
export type Schedule = z.infer<typeof scheduleSchema>;

const moneyFieldSchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (typeof value === "number") {
      return value.toString();
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? undefined : trimmed;
    }

    return value;
  },
  z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, "Amount must be a number with up to 2 decimals")
    .optional(),
);

/** Severity options available on the top-of-form match-strictness radios */
export type MatchStrictness = "none" | "flag" | "block";

export const jobPostSchema = z
  .object({
    id: z.string().uuid().optional(),
    publishedAt: z.string().datetime().optional(),
    visibility: z
      .nativeEnum(JobPostVisibility)
      .default(JobPostVisibility.Public),
    title: z.string().min(1, "Title of the position is required"),
    externalId: z.string().max(255).optional(),
    locationType: z
      .enum(["facility", "city-state", "remote"])
      .default("facility"),
    remote: z.boolean().nullable().optional(),
    facility: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
        address1: z.string().nullable(),
        address2: z.string().optional().nullable(),
        city: z.string().nullable(),
        state: z.string().nullable(),
        zipCode: z.string().nullable(),
        isRemote: z.boolean().nullable(),
      })
      .nullable()
      .optional(),
    city: z.string().nullable().optional(),
    stateCode: z.string().nullable().optional(),
    cityLocation: z
      .object({
        name: z.string(),
        lat: z.number().optional(),
        lng: z.number().optional(),
      })
      .nullable()
      .optional(),
    practiceSetting: z.nativeEnum(PracticeSetting).nullable().optional(),
    healthcareProfessionalTypes: z
      .array(
        z.object({
          id: z.string().uuid(),
          name: z.string(),
        }),
      )
      .optional(),
    wageMinAmount: moneyFieldSchema,
    wageMaxAmount: moneyFieldSchema,
    wageFrequencyId: z.nativeEnum(WageFrequency).nullable().optional(),
    guaranteedBasePay: z.boolean().optional(),
    guaranteedBasePayDescription: z.string().optional(),
    specialties: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
        }),
      )
      .optional(),
    jobDescription: z.string().optional(),
    responsibilities: z.array(z.string()).optional(),
    preferredQualifications: z.array(z.string()).optional(),
    /** Unified requirements list — replaces the old free-text requiredQualifications field */
    requirements: z.array(z.custom<RequirementRow>()).optional(),
    /**
     * Match-strictness for Professional Types. When "none" (default), no requirement
     * row is created. "flag" and "block" synthesize a ProfessionalType requirement
     * row at save time from the healthcareProfessionalTypes field values.
     */
    professionalTypeSeverity: z.enum(["none", "flag", "block"]).default("none"),
    /**
     * Match-strictness for Specialties. Same semantics as professionalTypeSeverity.
     */
    specialtySeverity: z.enum(["none", "flag", "block"]).default("none"),
    employmentTypeIds: z
      .array(z.nativeEnum(EmploymentType))
      .min(1, "At least one employment type is required"),
    startDateType: z.enum(["immediate", "specific"]).default("immediate"),
    startDate: z.string().optional(),
    endDateType: z.enum(["none", "specific"]).default("none"),
    endDate: z.string().optional(),
    scheduleDescription: z.string().optional(),
    organizationNotes: z.string().optional(),
    hiringProcessId: z.string().uuid().nullable().optional(),
    requireHiringProcessSelection: z.boolean().optional(),
    organizationBusinessUnitId: z.string().uuid().optional(),
  })
  .superRefine((data, ctx) => {
    // Validate fields based on location type
    if (data.locationType === "facility") {
      if (!data.facility) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select a facility",
          path: ["facility"],
        });
      }
    } else if (data.locationType === "city-state") {
      if (!data.cityLocation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "City is required",
          path: ["cityLocation"],
        });
      }
      if (!data.practiceSetting) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Practice setting is required",
          path: ["practiceSetting"],
        });
      }
    }

    if (
      data.requireHiringProcessSelection &&
      data.hiringProcessId === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a hiring process or None",
        path: ["hiringProcessId"],
      });
    }
  });

export type JobPostSchemaType = z.infer<typeof jobPostSchema>;
